import React, { useState, useEffect, useRef } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import RoleAssignCheckbox from "./RoleAssignCheckbox";

export default function RoleAdd({
  createRole,
  updateRole,
  getAllTheAvailableFeatures,
  assignFeatureToTheRole,
  isAssigning = false,
  roleToEdit = null,
  embedded = false,
  readOnly = false,
  onCancel,
  onComplete,
  onRoleAdded,
}) {
  const { t } = useTranslation();
  const featuresRef = useRef(null);
  const [formData, setFormData] = useState({
    name: roleToEdit?.name || "",
    description: roleToEdit?.description || "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [organizationId, setOrganizationId] = useState(null);
  const isEdit = Boolean(roleToEdit?.id);

  useEffect(() => {
    try {
      const userState = localStorage.getItem("userState");
      if (userState) {
        const parsedUserState = JSON.parse(userState);
        if (parsedUserState?.user?.organizationId) {
          setOrganizationId(parsedUserState.user.organizationId);
        }
      }
    } catch (error) {
      console.error("Error parsing userState from localStorage:", error);
    }
  }, []);

  useEffect(() => {
    setFormData({
      name: roleToEdit?.name || "",
      description: roleToEdit?.description || "",
    });
    setFormErrors({});
    setErrorMessage("");
  }, [roleToEdit?.id, roleToEdit?.name, roleToEdit?.description]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;

    const errors = {};
    if (!formData.name) {
      errors.name = "Role name is required";
    }
    if (!formData.description) {
      errors.description = "Description is required";
    }
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      let saved = null;
      if (isEdit) {
        await updateRole({
          id: roleToEdit.id,
          name: formData.name,
          description: formData.description,
        });
        if (featuresRef.current?.save) {
          await featuresRef.current.save();
        }
        saved = {
          ...roleToEdit,
          name: formData.name,
          description: formData.description,
        };
      } else {
        const result = await createRole({
          name: formData.name,
          description: formData.description,
          organizationId: organizationId,
        });

        setFormData({
          name: "",
          description: "",
        });
        saved = result?.role || result;
      }

      onRoleAdded?.(saved);
      onComplete?.(saved);
    } catch (error) {
      console.error("Error saving role:", error);
      setErrorMessage(
        isEdit
          ? "Failed to update role. Please try again."
          : "Failed to create role. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldStyles = {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      height: 55,
      borderRadius: "16px",
      borderWidth: 2,
      backgroundColor: readOnly ? "#f8fafc" : "#fff",
      "& fieldset": {
        borderWidth: "2px",
        borderColor: "rgba(24, 48, 89, 1)",
      },
      "&.Mui-error fieldset": {
        borderColor: "rgba(24, 48, 89, 1)",
      },
    },
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      style={{
        minHeight: embedded ? "auto" : "238px",
        position: "relative",
        borderRadius: 10,
        padding: embedded ? "20px" : "40px",
        margin: embedded ? 0 : "0px 40px",
        gap: 20,
        background: "#FFFFFF",
        border: "1px solid #A5DCF3",
        boxShadow: embedded ? "none" : "-2px 2px 5px 0px #00000040",
      }}
    >
      <Box
        sx={{
          height: 52,
          borderRadius: "8px",
          gap: 2,
          px: 3,
          py: 1.5,
          background: "#647490",
          boxShadow: "-2px 2px 5px 0px #00000040",
          fontFamily: "Roboto",
          fontWeight: 600,
          fontSize: {
            xs: "16px",
            sm: "18px",
            md: "20px",
            lg: "24px",
          },
          lineHeight: "100%",
          color: "#FFFFFF",
        }}
      >
        {t(
          readOnly
            ? "Role Details"
            : isEdit
              ? "Update Role"
              : "Create a New Role",
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "flex-end" },
          justifyContent: { xs: "center", md: "space-between" },
          width: "100%",
          marginTop: "20px",
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            width: "100%",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              flex: 1,
              minWidth: 0,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                marginBottom: "8px",
                fontWeight: 500,
                color: "#1F242E",
                fontFamily: "Roboto",
                fontSize: { xs: "18px", md: "20px" },
                lineHeight: "100%",
              }}
            >
              {t("role")}
              <Typography
                component="span"
                sx={{ color: "red", marginLeft: "4px" }}
              >
                *
              </Typography>
            </Typography>
            <TextField
              placeholder={t("Enter the role (e.g.Admin)")}
              variant="outlined"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              error={!!formErrors.name}
              helperText={formErrors.name}
              disabled={readOnly}
              InputLabelProps={{ shrink: false }}
              sx={fieldStyles}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              flex: 1,
              minWidth: 0,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                marginBottom: "8px",
                fontWeight: 500,
                color: "#1F242E",
                fontFamily: "Roboto",
                fontSize: { xs: "18px", md: "20px" },
                lineHeight: "100%",
              }}
            >
              {t("Description")}
              <Typography
                component="span"
                sx={{ color: "red", marginLeft: "4px" }}
              >
                *
              </Typography>
            </Typography>
            <TextField
              placeholder={t(
                "Enter role description (e.g.Manages user accounts)",
              )}
              variant="outlined"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              error={!!formErrors.description}
              helperText={formErrors.description}
              disabled={readOnly}
              InputLabelProps={{ shrink: false }}
              sx={fieldStyles}
            />
          </Box>
        </Box>
      </Box>

      {isEdit && getAllTheAvailableFeatures && assignFeatureToTheRole ? (
        <Box sx={{ mt: 3 }}>
          <Typography
            sx={{
              mb: 2,
              fontWeight: 600,
              color: "#1F242E",
              fontFamily: "Roboto",
              fontSize: { xs: "16px", md: "18px" },
            }}
          >
            {t("Permissions")}
          </Typography>
          <RoleAssignCheckbox
            ref={featuresRef}
            getAllTheAvailableFeatures={getAllTheAvailableFeatures}
            selectedRoleData={roleToEdit}
            assignFeatureToTheRole={assignFeatureToTheRole}
            isSubmitting={isAssigning}
            showActions={false}
            readOnly={readOnly}
          />
        </Box>
      ) : null}

      {errorMessage ? (
        <Typography sx={{ color: "#d32f2f", mt: 2, fontSize: "14px" }}>
          {t(errorMessage)}
        </Typography>
      ) : null}

      {!readOnly ? (
        <Box
          sx={{
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "24px",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: "16px", sm: "64px" },
          }}
        >
          {onCancel ? (
            <Button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              sx={{
                width: { xs: "100%", sm: "125px" },
                height: "55px",
                borderRadius: "8px",
                background: "#F2F2F3",
                border: "1px solid #414958",
                color: "#1F242E",
                textTransform: "none",
                fontSize: "20px",
                fontWeight: 600,
                "&:hover": { background: "#E0E0E1" },
              }}
            >
              {t("Cancel")}
            </Button>
          ) : null}
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              width: { xs: "100%", sm: onCancel ? "125px" : 112 },
              height: 55,
              borderRadius: "8px",
              padding: "16px 24px",
              border: "1px solid #414958",
              backgroundColor: "#F9E719",
              fontFamily: "Roboto",
              fontWeight: 600,
              fontSize: { xs: "18px", md: "20px" },
              lineHeight: "100%",
              color: "#1F242E",
              "&:hover": {
                backgroundColor: "#e0cf16",
              },
              "&:disabled": {
                backgroundColor: "#f9e71980",
                color: "#1f242e80",
              },
              textTransform: "none",
            }}
          >
            {isSubmitting ? t("Saving...") : t("Save")}
          </Button>
        </Box>
      ) : onCancel ? (
        <Box
          sx={{
            width: "100%",
            justifyContent: "center",
            display: "flex",
            marginTop: "24px",
          }}
        >
          <Button
            type="button"
            onClick={onCancel}
            sx={{
              width: "125px",
              height: "55px",
              borderRadius: "8px",
              background: "#F2F2F3",
              border: "1px solid #414958",
              color: "#1F242E",
              textTransform: "none",
              fontSize: "20px",
              fontWeight: 600,
            }}
          >
            {t("Close")}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}
